import i18nFactory from '@yandex-int/i18n';
import classNames from 'classnames';
import { TextInputSize } from 'lego-on-react';
import * as React from 'react';

import { mapStringToCurrencySymbol } from '../../mappers/common/common';
import { Bem } from '../../service/Bem/Bem';
import { getFormattedPrice } from '../../service/FormatHelpers/FormatHelpers';
import { BannersPriceField } from '../../typings/bannerPricesPopupState';
import { BannerPriceForDirect, PrefixPrice } from '../../typings/banners';
import { GdAdPriceCurrency } from '../../typings/graphql-grid';
import { DirectInputErrorsView } from '../DirectInputErrorsView/DirectInputErrorsView';
import { InputFieldType } from '../InputField/InputField';
import { RequiredMark } from '../RequiredMark/RequiredMark';
import { SelectDecorator } from '../SelectDecorator/SelectDecorator';

import './BannersPriceEditor.css';
import * as keyset from './BannersPriceEditor.i18n';

const i18n = i18nFactory(keyset);
const BLOCK = 'banners-price-editor';

type BannersPriceValueField =
    | BannersPriceField.PRICE
    | BannersPriceField.PRICE_OLD;

export interface BannersPriceEditorProps {
    bannerPrice: BannerPriceForDirect;
    onChange: (
        field: BannersPriceField,
        value: string,
        paths?: Array<string>
    ) => void;
    size?: TextInputSize;
    dilimiterPoint?: boolean;
    isPriceRequired?: boolean;
    mods?: {
        [key: string]: string | boolean;
    };
}

interface BannersPriceEditorState {
    price: string;
    priceOld: string;
}

export class BannersPriceEditor extends React.PureComponent<
    BannersPriceEditorProps,
    BannersPriceEditorState
> {
    state = {
        price: '',
        priceOld: ''
    };

    componentDidMount() {
        const { price, priceOld } = this.props.bannerPrice;
        const priceValue = price.text;
        const priceOldValue = priceOld.text;

        this.setState({
            price: getFormattedPrice(priceValue, this.props.dilimiterPoint),
            priceOld: getFormattedPrice(
                priceOldValue,
                this.props.dilimiterPoint
            )
        });
    }

    componentDidUpdate(prevProps: BannersPriceEditorProps) {
        this.deriveStateInProps(prevProps);
    }

    render() {
        return (
            <div className={Bem.cls({ block: BLOCK, mods: this.props.mods })}>
                <div
                    className={classNames(
                        Bem.cls(BLOCK, 'field-wrapper'),
                        Bem.cls(BLOCK, 'prefix-field')
                    )}
                >
                    <div className={Bem.cls(BLOCK, 'select-title')}>
                        {i18n('prefix')}
                    </div>
                    <div className={Bem.cls(BLOCK, 'select-content')}>
                        {this.getPrefixPrice()}
                    </div>
                </div>
                <div
                    className={classNames(
                        Bem.cls(BLOCK, 'field-wrapper'),
                        Bem.cls(BLOCK, 'price-field')
                    )}
                >
                    <div className={Bem.cls(BLOCK, 'input-title')}>
                        {i18n('price')}
                        {this.props.isPriceRequired ? (
                            <RequiredMark
                                cls={Bem.cls(BLOCK, 'required-mark')}
                            />
                        ) : null}
                    </div>
                    <div className={Bem.cls(BLOCK, 'input-content')}>
                        {this.getPrice(BannersPriceField.PRICE)}
                    </div>
                </div>
                <div
                    className={classNames(
                        Bem.cls(BLOCK, 'field-wrapper'),
                        Bem.cls(BLOCK, 'currency-field')
                    )}
                >
                    <div className={Bem.cls(BLOCK, 'select-title')}>
                        {i18n('currency')}
                    </div>
                    <div className={Bem.cls(BLOCK, 'select-content')}>
                        {this.getCurrency()}
                    </div>
                </div>
                <div
                    className={classNames(
                        Bem.cls(BLOCK, 'field-wrapper'),
                        Bem.cls(BLOCK, 'priceOld-field')
                    )}
                >
                    <div className={Bem.cls(BLOCK, 'input-title')}>
                        {i18n('priceOld')}
                    </div>
                    <div className={Bem.cls(BLOCK, 'input-content')}>
                        {this.getPrice(BannersPriceField.PRICE_OLD)}
                    </div>
                </div>
            </div>
        );
    }

    private getPrefixPrice() {
        const { size = 'n' } = this.props,
            prefixValue = this.props.bannerPrice.prefix,
            prefixOptions = [
                [PrefixPrice.DEFAULT, i18n('default')],
                [PrefixPrice.FROM, i18n('from')],
                [PrefixPrice.TO, i18n('to')]
            ].map(([prefix, text]: [PrefixPrice, string]) => ({
                val: prefix,
                text: text
            }));

        return (
            <SelectDecorator
                cls={Bem.cls(BLOCK, 'select-wrap')}
                items={prefixOptions}
                val={PrefixPrice[prefixValue] || PrefixPrice.DEFAULT}
                onChange={(value: string[]) =>
                    this.onSelectChange(BannersPriceField.PREFIX, value)
                }
                size={size}
                width={'max'}
                popup={{ hiding: false }}
                view={'classic'}
                theme={'normal'}
                type={'radio'}
                button={{ pin: 'brick-brick' }}
            />
        );
    }

    private deriveStateInProps = (prevProps: BannersPriceEditorProps) => {
        if (
            this.props.bannerPrice.price.text !==
            prevProps.bannerPrice.price.text
        ) {
            this.setState({
                price: getFormattedPrice(
                    this.props.bannerPrice.price.text,
                    this.props.dilimiterPoint
                )
            });
        }

        if (
            this.props.bannerPrice.priceOld.text !==
            prevProps.bannerPrice.priceOld.text
        ) {
            this.setState({
                priceOld: getFormattedPrice(
                    this.props.bannerPrice.priceOld.text,
                    this.props.dilimiterPoint
                )
            });
        }
    };

    private getPrice(fieldPrice: BannersPriceValueField) {
        const path = fieldPrice === 'price' ? '[0].price' : '[0].priceOld';

        return (
            <DirectInputErrorsView
                text={this.state[fieldPrice]}
                hasClear={true}
                path={path}
                inputType={InputFieldType.TEXT}
                size={this.props.size || 'n'}
                controlAttrs={{ maxLength: 21 }}
                theme={'normal'}
                onChange={(value: string) =>
                    this.onPriceChange(fieldPrice, value)
                }
                onBlur={() => this.onPriceBlur(fieldPrice)}
            />
        );
    }

    private getCurrency() {
        const { size = 'n', bannerPrice } = this.props,
            currencyValue = bannerPrice.currency,
            currencyOptions = [
                [GdAdPriceCurrency.BYN, i18n('BYN')],
                [GdAdPriceCurrency.USD, i18n('USD')],
                [GdAdPriceCurrency.EUR, i18n('EUR')],
                [GdAdPriceCurrency.KZT, i18n('KZT')],
                [GdAdPriceCurrency.RUB, i18n('RUB')],
                [GdAdPriceCurrency.TRY, i18n('TRY')],
                [GdAdPriceCurrency.UZS, i18n('UZS')],
                [GdAdPriceCurrency.UAH, i18n('UAH')],
                [GdAdPriceCurrency.CHF, i18n('CHF')]
            ].map(([cur, text]: [GdAdPriceCurrency, string]) => ({
                val: cur,
                text: `${mapStringToCurrencySymbol(cur)} \u2014 ${text}`
            }));

        return (
            <SelectDecorator
                cls={Bem.cls(BLOCK, 'select-wrap')}
                items={currencyOptions}
                val={GdAdPriceCurrency[currencyValue]}
                onChange={(value: string[]) =>
                    this.onSelectChange(BannersPriceField.CURRENCY, value)
                }
                size={size}
                view={'classic'}
                theme={'normal'}
                type={'radio'}
                width={'max'}
                popup={{ hiding: false }}
                button={{ pin: 'brick-brick' }}
            />
        );
    }

    private onSelectChange = (field: BannersPriceField, value: string[]) => {
        const { onChange } = this.props;

        onChange(field, value[0]);
    };

    private onPriceChange = (field: BannersPriceValueField, value: string) => {
        this.setState({
            ...this.state,
            [field]: value
        });
    };

    private onPriceBlur = (field: BannersPriceValueField) => {
        const dilimiterPoint = this.props.dilimiterPoint,
            price = this.state[field],
            formattedPrice = getFormattedPrice(price, dilimiterPoint),
            priceFromProps = this.props.bannerPrice[field].text,
            formattedPriceFromProps = getFormattedPrice(
                priceFromProps,
                dilimiterPoint
            );

        if (priceFromProps === price) {
            return;
        }

        if (formattedPriceFromProps === formattedPrice) {
            return this.onPriceChange(field, formattedPrice);
        }

        this.props.onChange(field, formattedPrice, ['[0].priceOld']);
    };
}
